import { ApiError } from "@/lib/api";
import type { AuthedRequester } from "@/lib/brokerAuth";
import { formatBondDate } from "@/lib/bondValueDisplay";
import { documentTypeLabel } from "@/lib/brokerDocumentTypes";
import { sentenceCaseKey } from "@/lib/listFormat";

// BRK-S187 — the broker's READ-ONLY invoice surface, consuming PMS-S507.
//
// One invoice per order, 45-day terms, billed direct to the contractor (plan
// rule 5). The broker sees the number, the bonds, the amount, the dates, a
// broker-safe status and the documents — and NOTHING about payment method,
// reference, who recorded it, an underwriter's reason or note. There is no Pay
// button (the payment provider is SIG-S020).
//
// Both routes are pinned since the sprint-10 release re-pin (PMS-S502/S503;
// `test/lib/invoicesContract.test.ts` binds these types to it). The status
// vocabulary is a TOLERANT closed mirror — an unknown status reads
// "Status unavailable", never a blank (the BRK-S114 lesson).

export const INVOICE_STATUSES = [
  "due",
  "paid",
  "overdue",
  "awaiting_decision",
] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export interface InvoiceBondRef {
  kind: string;
  bondNumber: string | null;
}

/** One row of `GET /api/broker/accounts/{id}/invoices`. */
export interface InvoiceRow {
  id: string;
  number: string;
  bondSubmissionId: string | null;
  sbNumber: string | null;
  bonds: InvoiceBondRef[];
  amount: number;
  issuedOn: string;
  dueOn: string;
  /** Open on the wire; read through `readInvoiceStatus`. */
  status: string;
  /** What the invoice bills, as PMS words it ("Premium · Performance &
   *  payment bonds") — the Invoices box's Description column (BRK-S500,
   *  PMS-S854). */
  description: string;
}

/** The invoice's documents, downloaded through the account document route. */
export interface InvoiceDocument {
  type: string;
  documentId: string;
  filename: string;
}

/** `GET /api/broker/invoices/{id}`. */
export interface InvoiceDetail {
  id: string;
  number: string;
  account: { id: string; name: string };
  bondSubmissionId: string | null;
  sbNumber: string | null;
  bonds: (InvoiceBondRef & {
    obligeeName?: string | null;
    projectName?: string | null;
  })[];
  amount: number;
  issuedOn: string;
  dueOn: string;
  status: string;
  /** PMS-S854 — `description` (see `InvoiceRow`). */
  description: string;
  uccWarningSentAt: string | null;
  paidOn: string | null;
  documents: InvoiceDocument[];
}

export function readInvoiceStatus(raw: unknown): InvoiceStatus | null {
  return (INVOICE_STATUSES as readonly unknown[]).includes(raw)
    ? (raw as InvoiceStatus)
    : null;
}

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  due: "Due",
  paid: "Paid",
  overdue: "Overdue",
  awaiting_decision: "Under review",
};

/** The chip's words. Unknown says so, never a blank. */
export function invoiceStatusLabel(status: InvoiceStatus | null): string {
  return status === null ? "Status unavailable" : STATUS_LABELS[status];
}

const BOND_KIND_LABELS: Readonly<Record<string, string>> = {
  performance: "Performance",
  payment: "Payment",
};

/** "Performance SSB-2026-000184" — the kind, labelled, and its number. */
export function invoiceBondLabel(bond: InvoiceBondRef): string {
  const kind = Object.hasOwn(BOND_KIND_LABELS, bond.kind)
    ? BOND_KIND_LABELS[bond.kind]
    : sentenceCaseKey(bond.kind);
  return bond.bondNumber ? `${kind} ${bond.bondNumber}` : kind;
}

/** An invoice amount, with cents when it has any (D-10: display only). */
export function formatInvoiceAmount(amount: number): string {
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/** Whole days from `today` to an ISO date (negative once past). */
export function daysUntil(isoDate: string, today: Date): number {
  const [y, m, d] = isoDate.split("T")[0].split("-").map(Number);
  const due = Date.UTC(y, m - 1, d);
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((due - now) / 86_400_000);
}

/**
 * The detail's status lines, in reading order. Pure, so each state is
 * testable without a render; `today` is injected for the days-remaining count.
 */
export function invoiceStatusLines(
  invoice: Pick<
    InvoiceDetail,
    "status" | "dueOn" | "paidOn" | "uccWarningSentAt"
  >,
  today: Date,
): string[] {
  const status = readInvoiceStatus(invoice.status);
  const overdue = [`Overdue since ${formatBondDate(invoice.dueOn)}`];
  if (invoice.uccWarningSentAt) {
    overdue.push(
      `A UCC warning letter was sent on ${formatBondDate(invoice.uccWarningSentAt)}`,
    );
  }
  switch (status) {
    case "due": {
      const days = daysUntil(invoice.dueOn, today);
      const remaining =
        days > 1
          ? `${String(days)} days remaining`
          : days === 1
            ? "1 day remaining"
            : days === 0
              ? "Due today"
              : null;
      return [
        `Due ${formatBondDate(invoice.dueOn)}`,
        ...(remaining ? [remaining] : []),
      ];
    }
    case "paid":
      return invoice.paidOn
        ? [`Paid ${formatBondDate(invoice.paidOn)}`]
        : ["Paid"];
    case "overdue":
      return overdue;
    case "awaiting_decision":
      return [...overdue, "Your underwriter is reviewing next steps"];
    default:
      return ["Status unavailable"];
  }
}

/** A download's label — the document-type mirror's words (BRK-S188), which a
 *  type this build has not heard of still gets humanised from. */
export function invoiceDocumentLabel(type: string): string {
  return documentTypeLabel([], type);
}

/** Where an invoice's detail lives. */
export function invoiceHref(id: string): string {
  return `/surety/invoices/${encodeURIComponent(id)}`;
}

/**
 * GET /api/broker/accounts/{id}/invoices — newest first, own-scoped.
 * The envelope is `{invoices}` (pinned); anything else is a handled error,
 * never a "no invoices" that is a lie about the insured's money.
 */
export async function listAccountInvoices(
  request: AuthedRequester,
  accountId: string,
): Promise<InvoiceRow[]> {
  const body = await request<{ invoices?: unknown }>(
    `/api/broker/accounts/${encodeURIComponent(accountId)}/invoices`,
  );
  const rows = Array.isArray(body?.invoices) ? body.invoices : null;
  if (rows === null) {
    throw new ApiError(
      200,
      "We couldn’t read this insured’s invoices — reload the page to try again.",
      body,
    );
  }
  return rows as InvoiceRow[];
}

/** GET /api/broker/invoices/{id} — one invoice, own-scoped (404 otherwise). */
export function getInvoice(
  request: AuthedRequester,
  id: string,
): Promise<InvoiceDetail> {
  return request<InvoiceDetail>(
    `/api/broker/invoices/${encodeURIComponent(id)}`,
  );
}
